/**
 * Core types for the flat, typed state machine.
 *
 * The machine config is a pure, serializable value: states, guarded transitions,
 * `assign` context updates, and per-state `invoke`. Service and guard
 * implementations are referenced by name and injected at interpretation time
 * (see `interpret` / `useStateMachine`), which keeps the config pure so it can
 * be typed, diagrammed (`toMermaid`), logged, and unit-tested without React.
 */

/** An event is a `type` discriminant plus an arbitrary payload. */
export type EventObject = { type: string } & Record<string, unknown>;

/** Produced by `assign()`. Applied on a transition to compute the next context. */
export type Assigner<TContext, TEvent extends EventObject = EventObject> = {
  type: '@flow/assign';
  assignment: (context: TContext, event: TEvent) => Partial<TContext>;
};

/** v1 supports assignment actions (context updates). */
export type Action<TContext, TEvent extends EventObject = EventObject> = Assigner<
  TContext,
  TEvent
>;

/**
 * A transition target. Either a bare state name, or an object with an optional
 * `target` (omitted = internal transition, stay in place), a named `guard`, and
 * `assign` `actions`.
 */
export type TransitionConfig<TContext, TEvent extends EventObject = EventObject> =
  | string
  | {
      target?: string;
      guard?: string;
      actions?: Action<TContext, TEvent> | Action<TContext, TEvent>[];
    };

/** Async side-effect owned by a state. Resolves → `onDone`, rejects → `onError`. */
export type InvokeConfig<TContext, TEvent extends EventObject = EventObject> = {
  src: string;
  onDone?: TransitionConfig<TContext, TEvent>;
  onError?: TransitionConfig<TContext, TEvent>;
};

export type StateNodeConfig<TContext, TEvent extends EventObject = EventObject> = {
  on?: Record<string, TransitionConfig<TContext, TEvent>>;
  invoke?: InvokeConfig<TContext, TEvent>;
  /** A `final` state is terminal — no further invokes run. */
  type?: 'final';
};

export type MachineConfig<TContext, TEvent extends EventObject = EventObject> = {
  id: string;
  initial: string;
  context: TContext;
  states: Record<string, StateNodeConfig<TContext, TEvent>>;
};

/** The value produced by `createStateMachine`. `TValue` is the union of state names. */
export type StateMachine<TContext, TValue extends string = string> = {
  id: string;
  initial: TValue;
  context: TContext;
  states: Record<string, StateNodeConfig<TContext, EventObject>>;
  /** All declared state names — the discriminated union backing `state.value`. */
  stateKeys: string[];
};

/** A snapshot of the running machine. `value` is the current phase. */
export type State<TContext, TValue extends string = string> = {
  value: TValue;
  context: TContext;
  /** True when `value` is a `type: 'final'` state. */
  done: boolean;
  matches: (value: TValue) => boolean;
  /** Whether the current state declares an `on` handler for this event type. */
  can: (event: string) => boolean;
};

/**
 * An invoked service. Receives the current context and an `AbortSignal`, and
 * returns a value or a promise of a value (the interpreter wraps it) — the
 * resolved value becomes the `onDone` event's `data`.
 */
export type InvokeService<TContext> = (
  context: TContext,
  meta: { signal: AbortSignal }
) => unknown;

export type GuardFn<TContext, TEvent extends EventObject = EventObject> = (
  context: TContext,
  event: TEvent
) => boolean;

/** Emitted on every taken transition — the backbone for logging + telemetry. */
export type TransitionRecord = { from: string; event: string; to: string };

/** Implementations injected at interpretation time (kept out of the pure config). */
export type Implementations<TContext, TEvent extends EventObject = EventObject> = {
  services?: Record<string, InvokeService<TContext>>;
  guards?: Record<string, GuardFn<TContext, TEvent>>;
  input?: Partial<TContext>;
  /**
   * Called on every taken transition. The record identifies the transition
   * (and is all a log sink should ever see); `context` is the machine context
   * AFTER the transition's assigns, passed separately so a hook can route on
   * invoke outcomes without a side-channel copy. Stale settlements never
   * transition (run-id guard), so it is always the live invocation's.
   */
  inspect?: (record: TransitionRecord, context: TContext) => void;
};

/** Internal event types dispatched when an invoked service settles. */
export const DONE_EVENT = 'flow.invoke.done';
export const ERROR_EVENT = 'flow.invoke.error';

/**
 * The event an invoke's `onDone` transition receives — `data` is the service's
 * resolved value. Use as `assign<Ctx, InvokeDoneEvent<Region>>((ctx, event) => ...)`.
 */
export type InvokeDoneEvent<TData = unknown> = {
  type: typeof DONE_EVENT;
  data: TData;
};

/**
 * The event an invoke's `onError` transition receives — `error` is what the
 * service threw or rejected with.
 */
export type InvokeErrorEvent<TError = unknown> = {
  type: typeof ERROR_EVENT;
  error: TError;
};
